import { createFileRoute, useRouter, useRouterState, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";
import eeswebLogo from "@/assets/eesweb-logo.png";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Sign in — EESWEB" }] }),
  component: AuthPage,
});

/** Only same-app paths: "/calls/x" yes; "//evil.com", "https://..." or "/auth" no. */
function safeRedirect(v: unknown): string {
  const s = typeof v === "string" ? v : "";
  if (!s.startsWith("/") || s.startsWith("//") || s.startsWith("/\\") || s.startsWith("/auth")) return "/dashboard";
  return s;
}

/** Whether this email belongs to an account moved from the old panel that has no password yet. */
async function needsPassword(email: string): Promise<boolean> {
  try {
    const r = await fetch("/api/public/password-status", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
    return r.ok && (await r.json()).needsPassword === true;
  } catch { return false; }
}

async function clearNeedsPassword() {
  try {
    const { firebaseAuth } = await import("@/integrations/firebase/client");
    const u = firebaseAuth().currentUser;
    if (u) await fetch("/api/public/password-status", { method: "DELETE", headers: { Authorization: `Bearer ${await u.getIdToken()}` } });
  } catch { /* best effort: the note only shows after a failed sign-in anyway */ }
}

function AuthPage() {
  const { user, loading, signIn, signUp } = useAuth();
  const router = useRouter();
  const redirectNow = useRouterState({ select: (st) => safeRedirect((st.location.search as Record<string, unknown>).redirect) });
  // Read the target once: after we navigate away this page renders one more
  // time with the NEW location (no ?redirect=), which must not re-route to
  // the dashboard.
  const [redirect] = useState(redirectNow);
  const done = useRef(false);
  useEffect(() => {
    // history.replace takes a ready-made path (+ search), and replacing keeps
    // the sign-in form out of the back-button history.
    if (!loading && user && !done.current) {
      done.current = true;
      router.history.replace(redirect);
    }
  }, [user, loading, router, redirect]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  // Set after a failed sign-in by an account moved from the old panel that
  // hasn't set a password yet; "sent" once its password email has gone out.
  const [pwNote, setPwNote] = useState<null | "needed" | "sent">(null);

  // The sign-in fields are uncontrolled so text typed before hydration isn't
  // wiped; read what is actually in them rather than state.
  const fieldValue = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? "";

  const onSignIn = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try {
      await signIn(fieldValue("se"), fieldValue("sp"));
      toast.success("Welcome back");
      // They have a password now, so stop showing them the set-password note.
      void clearNeedsPassword();
    }
    catch (err) {
      const code = (err as { code?: string }).code ?? "";
      if (["auth/invalid-credential", "auth/wrong-password", "auth/user-not-found", "auth/invalid-login-credentials"].includes(code)) {
        if (await needsPassword(fieldValue("se"))) {
          setPwNote((n) => n ?? "needed");
          toast.error("This account hasn't set a password on the new panel yet.");
        } else {
          toast.error("Wrong email or password.");
        }
      } else if (code === "auth/too-many-requests") {
        toast.error("Too many attempts — wait a minute, or reset your password.");
      } else {
        toast.error(err instanceof Error ? err.message : "Sign-in failed");
      }
    }
    finally { setBusy(false); }
  };
  // Users moved over from the old (Supabase) panel have no password here yet,
  // so "Forgot password" is also how they set their first one.
  const onReset = async () => {
    const email = fieldValue("se");
    if (!email.trim()) return toast.error("Enter your email above first");
    setBusy(true);
    try {
      const { sendPasswordResetEmail } = await import("firebase/auth");
      const { firebaseAuth } = await import("@/integrations/firebase/client");
      await sendPasswordResetEmail(firebaseAuth(), email.trim());
      if (pwNote) setPwNote("sent");
      toast.success("If that email has an account, a password link is on its way.");
    } catch (err) {
      const code = (err as { code?: string }).code;
      // Don't reveal whether an account exists.
      if (code === "auth/user-not-found") toast.success("If that email has an account, a password link is on its way.");
      else toast.error(code === "auth/invalid-email" ? "That email address isn't valid" : "Couldn't send the email — try again");
    } finally { setBusy(false); }
  };
  const onSignUp = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try { await signUp(email, password, fullName); toast.success("Account created. An admin will assign you to a client."); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Sign-up failed"); }
    finally { setBusy(false); }
  };

  return (
    <main className="grid min-h-screen place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-2 text-center">
          <img src={eeswebLogo} alt="EESWEB" className="h-10 w-auto dark:brightness-0 dark:invert dark:opacity-90" />
          <p className="text-sm text-muted-foreground">Live CDR + recordings</p>
        </div>
        <Card className="rounded-2xl shadow-soft">
          <CardHeader>
            <CardTitle>Access your dashboard</CardTitle>
            <CardDescription>Sign in or create an account to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="signin">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">Sign in</TabsTrigger>
                <TabsTrigger value="signup">Sign up</TabsTrigger>
              </TabsList>
              <TabsContent value="signin">
                <form onSubmit={onSignIn} className="space-y-3 mt-4">
                  <div className="space-y-1.5"><Label htmlFor="se">Email</Label><Input id="se" type="email" autoComplete="email" inputMode="email" className="h-11" required defaultValue={email} onChange={(e) => setEmail(e.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor="sp">Password</Label><Input id="sp" type="password" autoComplete="current-password" className="h-11" required defaultValue={password} onChange={(e) => setPassword(e.target.value)} /></div>
                  {pwNote && (
                    <div role="status" data-testid="set-password-note" className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-sm leading-relaxed">
                      {pwNote === "needed" ? (
                        <>Your account moved to the new panel and needs a new password, just this once. Tap <strong>Forgot password</strong> below and we'll email you a link to set it.</>
                      ) : (
                        <>Check your email for the link, set your password, then sign in here with it.</>
                      )}
                    </div>
                  )}
                  <Button type="submit" disabled={busy} className="h-11 w-full">{busy ? "Signing in…" : "Sign in"}</Button>
                  <button type="button" onClick={onReset} disabled={busy} className="w-full rounded-md py-1 text-center text-sm text-primary hover:underline" data-testid="forgot-password">
                    Forgot password?
                  </button>
                </form>
              </TabsContent>
              <TabsContent value="signup">
                <form onSubmit={onSignUp} className="space-y-3 mt-4">
                  <div className="space-y-1.5"><Label htmlFor="un">Full name</Label><Input id="un" autoComplete="name" className="h-11" required value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor="ue">Email</Label><Input id="ue" type="email" autoComplete="email" inputMode="email" className="h-11" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                  <div className="space-y-1.5"><Label htmlFor="up">Password</Label><Input id="up" type="password" autoComplete="new-password" className="h-11" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                  <Button type="submit" disabled={busy} className="h-11 w-full">{busy ? "Creating…" : "Create account"}</Button>
                  <p className="text-center text-xs text-muted-foreground">New accounts see calls once EESWEB links them to a dealership.</p>
                </form>
              </TabsContent>
            </Tabs>
            <p className="mt-5 border-t pt-4 text-center text-sm text-muted-foreground">
              Just looking? <Link to="/demo" className="font-medium text-primary hover:underline">See a demo with sample calls</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
